import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Modal, ModalHeader, ModalBody, Footer } from './styles';
import { Box, Grid, IconButton, TextField } from '@material-ui/core';
import { Edit } from '@material-ui/icons';
import MaterialTable, { Column } from 'material-table';
import Botao from '../Botoes/Botao';
import { config } from '../Tabela';
import Formatter from '../../services/Formatter';
import useStore from '../../services/hooks/useStore';
import Select from '../Select';
import ModalNFEAvulsaItem, { NfeAvulsaItem } from './ModalNFEAvulsaItem';
import { apiV2 } from '../../services/utils';
import { abrirDanfePreview } from '../DanfePreview';
import X9Logger from '../../services/X9Logger';

export type NFeTesteData = {
    numeroNota?: string | number;
    serie?: string | number;
    parceiroLabel?: string;
    parceiroNome?: string;
    dataEmissao?: string;
    dataSaidaEntrada?: string;
    chaveReferenciada?: string;
    informacoesComplementares?: string;
    valorTotal?: number;
};

type Props = {
    open: boolean;
    handleClose: () => void;
    data?: NFeTesteData;
    tipo?: 'ENTRADA' | 'CIAP';
    /** Após enviar à SEFAZ com sucesso (para atualizar status na listagem). */
    onEnvioEmissaoSuccess?: (notaFiscalId: string | number) => void;
};

function mensagemErroApi(error: any, fallback: string): string {
    const d = error?.response?.data;
    if (!d) return fallback;
    if (Array.isArray(d.erros)) return d.erros.join(', ');
    if (typeof d.erros === 'string') return d.erros;
    if (d.erro) return typeof d.erro === 'string' ? d.erro : JSON.stringify(d.erro);
    if (typeof d === 'string') return d;
    if (typeof d === 'object') {
        const partes = Object.entries(d).map(([campo, msgs]) => {
            const m = Array.isArray(msgs) ? msgs.join(' ') : String(msgs);
            return `${campo}: ${m}`;
        });
        if (partes.length) return partes.join(' | ');
    }
    return fallback;
}

/** Converte `dd/MM/aaaa` para ISO 8601 com sufixo `Z` (contrato Swagger). */
function dataBrParaIso8601(dataStr: string): string {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((dataStr || '').trim());
    if (!m) {
        return new Date().toISOString();
    }
    const [, dd, mm, yyyy] = m;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd), 12, 0, 0, 0);
    return d.toISOString();
}

/** Base de cálculo simplificada: valor total do item × alíquota % (pré-visualização / total da nota). */
function calcularValorIcmsItem(valorTotalItem: number, aliquotaPercent: number): number {
    const vt = Number(valorTotalItem) || 0;
    const p = Number(aliquotaPercent) || 0;
    return +((vt * p) / 100).toFixed(2);
}

function dataBrValida(dataStr: string): boolean {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((dataStr || '').trim());
    if (!m) return false;
    const dd = Number(m[1]);
    const mm = Number(m[2]);
    const yyyy = Number(m[3]);
    const dt = new Date(yyyy, mm - 1, dd);
    return dt.getFullYear() === yyyy && dt.getMonth() === mm - 1 && dt.getDate() === dd;
}

function apenasDigitos(doc: string | null | undefined): string {
    return String(doc ?? '').replace(/\D/g, '');
}

type LinhaItemNfeAvulsa = NfeAvulsaItem & { _index: number };

const ModalNFeTeste = observer(({ open, handleClose, data, tipo = 'ENTRADA', onEnvioEmissaoSuccess }: Props) => {
    const store = useStore();

    const [numeroNota, setNumeroNota] = useState<string>(data?.numeroNota?.toString() || '');
    const [serie, setSerie] = useState<string>(data?.serie?.toString() || '');
    const [fornecedor, setFornecedor] = useState<SelectRow | null>(null);
    const [dataEmissao, setDataEmissao] = useState<string>(data?.dataEmissao || '');
    const [dataSaidaEntrada, setDataSaidaEntrada] = useState<string>(data?.dataSaidaEntrada || '');
    const [chaveReferenciada, setChaveReferenciada] = useState<string>(data?.chaveReferenciada || '');
    const [informacoesComplementares, setInformacoesComplementares] = useState<string>(data?.informacoesComplementares || '');
    const [itens, setItens] = useState<NfeAvulsaItem[]>([]);
    const [modalItemOpen, setModalItemOpen] = useState(false);
    const [itemEmEdicaoIndex, setItemEmEdicaoIndex] = useState<number | null>(null);

    const [notaFiscalAvulsaId, setNotaFiscalAvulsaId] = useState<string | number | null>(null);
    const [acaoAvulsaLoading, setAcaoAvulsaLoading] = useState<'visualizar' | 'enviar' | null>(null);
    const [configuracaoNfeId, setConfiguracaoNfeId] = useState<number | null>(null);

    useEffect(() => {
        if (!open) return;

        if (store.propriedade?.id) {
            store.fornecedores.populate(true).catch(() => null);
        }
        setNumeroNota('');
        setSerie('');
        setFornecedor(null);
        setDataEmissao(Formatter.dateToString(new Date(), '/').slice(0, 10));
        setDataSaidaEntrada(
            data?.dataSaidaEntrada?.trim() || Formatter.dateToString(new Date(), '/').slice(0, 10),
        );
        setChaveReferenciada(data?.chaveReferenciada || '');
        setInformacoesComplementares(data?.informacoesComplementares || '');
        setItens([]);
        setModalItemOpen(false);
        setItemEmEdicaoIndex(null);
        setNotaFiscalAvulsaId(null);
        setAcaoAvulsaLoading(null);
        setConfiguracaoNfeId(null);
    }, [open, data, store.propriedade?.id]);

    useEffect(() => {
        if (!open || !store.propriedade?.id) return;
        let cancelled = false;
        (async () => {
            try {
                const { data: res } = await apiV2.get('/configuracao-nfe/', {
                    params: { prop: store.propriedade!.id },
                });
                const list = res.results ?? res ?? [];
                const arr = Array.isArray(list) ? list : [];
                const first = arr[0];
                const id = first?.id != null ? Number(first.id) : null;
                const proximo =
                    first?.proximo_numero ??
                    (first as { proximoNumero?: string | number })?.proximoNumero;
                const serieCfg =
                    first?.serie_nfe ??
                    (first as { serie?: string | number })?.serie ??
                    (first as { serieNfe?: string | number })?.serieNfe;
                if (!cancelled) {
                    if (id != null && !Number.isNaN(id)) {
                        setConfiguracaoNfeId(id);
                    } else {
                        setConfiguracaoNfeId(null);
                    }
                    setSerie(serieCfg != null ? String(serieCfg) : '');
                    setNumeroNota(proximo != null ? String(proximo) : '');
                }
            } catch (err) {
                X9Logger.report(err as Error);
                if (!cancelled) {
                    setConfiguracaoNfeId(null);
                    setSerie('');
                    setNumeroNota('');
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, store.propriedade?.id, data]);

    const fornecedorOptions = useMemo(() => {
        return store.fornecedores.getSelectRows();
    }, [store.fornecedores.arr]);

    const valorTotal = useMemo(() => {
        return itens.reduce((acc, it) => acc + (Number(it.valorTotal) || 0), 0);
    }, [itens]);

    const valorIcmsTotalCalculado = useMemo(() => {
        return +itens
            .reduce((acc, it) => acc + calcularValorIcmsItem(Number(it.valorTotal) || 0, Number(it.icmsPercentual) || 0), 0)
            .toFixed(2);
    }, [itens]);

    const dadosItensTabela = useMemo(
        (): LinhaItemNfeAvulsa[] => itens.map((row, index) => ({ ...row, _index: index })),
        [itens],
    );

    const titulo = 'Gerar Nota Fiscal';

    const abrirNovoItem = () => {
        setItemEmEdicaoIndex(null);
        setModalItemOpen(true);
    };

    const editarItem = useCallback((index: number) => {
        setItemEmEdicaoIndex(index);
        setModalItemOpen(true);
    }, []);

    const colunasItensTabela = useMemo<Column<LinhaItemNfeAvulsa>[]>(
        () => [
            {
                field: 'codigoProduto',
                title: 'Código',
                align: 'left',
                ...(true && ({ width: 100 } as object)),
                render: (row) => <p style={{ textAlign: 'left', margin: 0 }}>{row.codigoProduto || '-'}</p>,
            },
            {
                field: 'descricaoProduto',
                title: 'Descrição do Produto',
                align: 'left',
                ...(true && ({ width: 220 } as object)),
                render: (row) => <p style={{ textAlign: 'left', margin: 0 }}>{row.descricaoProduto || '-'}</p>,
            },
            {
                field: 'codigoNcm',
                title: 'NCM',
                align: 'left',
                ...(true && ({ width: 100 } as object)),
                render: (row) => <p style={{ textAlign: 'left', margin: 0 }}>{row.codigoNcm || '-'}</p>,
            },
            {
                field: 'icmsCst',
                title: 'CST',
                align: 'left',
                ...(true && ({ width: 56 } as object)),
                render: (row) => <p style={{ textAlign: 'left', margin: 0 }}>{row.icmsCst || '-'}</p>,
            },
            {
                field: 'cfop',
                title: 'CFOP',
                align: 'left',
                ...(true && ({ width: 72 } as object)),
                render: (row) => <p style={{ textAlign: 'left', margin: 0 }}>{row.cfop || '-'}</p>,
            },
            {
                field: 'quantidade',
                title: 'Qtde',
                align: 'center',
                ...(true && ({ width: 88 } as object)),
                render: (row) => (
                    <p style={{ textAlign: 'center', margin: 0 }}>{Formatter.formatNumber(3, row.quantidade)}</p>
                ),
            },
            {
                field: 'valorUnitario',
                title: 'Vr Unit',
                align: 'center',
                ...(true && ({ width: 96 } as object)),
                render: (row) => (
                    <p style={{ textAlign: 'center', margin: 0 }}>{Formatter.formatNumber(2, row.valorUnitario)}</p>
                ),
            },
            {
                field: 'valorTotal',
                title: 'Vr Total',
                align: 'right',
                ...(true && ({ width: 100 } as object)),
                render: (row) => (
                    <p style={{ textAlign: 'right', margin: 0 }}>{Formatter.formatNumber(2, row.valorTotal)}</p>
                ),
            },
            {
                field: 'valorIcmsCalculado',
                title: 'Vr ICMS',
                align: 'right',
                ...(true && ({ width: 88 } as object)),
                render: (row) => (
                    <p style={{ textAlign: 'right', margin: 0 }}>
                        {Formatter.formatNumber(
                            2,
                            calcularValorIcmsItem(Number(row.valorTotal) || 0, Number(row.icmsPercentual) || 0),
                        )}
                    </p>
                ),
            },
            {
                field: 'icmsPercentual',
                title: 'Al ICMS',
                align: 'right',
                ...(true && ({ width: 88 } as object)),
                render: (row) => (
                    <p style={{ textAlign: 'right', margin: 0 }}>{Formatter.formatNumber(2, row.icmsPercentual)}%</p>
                ),
            },
            {
                field: 'acoes',
                title: 'Ações',
                align: 'center',
                sorting: false,
                disableClick: true,
                ...(true && ({ width: 72 } as object)),
                render: (row) => (
                    <IconButton onClick={() => editarItem(row._index)} size="small" disabled={Boolean(acaoAvulsaLoading)}>
                        <Edit fontSize="small" />
                    </IconButton>
                ),
            },
        ],
        [acaoAvulsaLoading, editarItem],
    );

    const salvarItem = (item: NfeAvulsaItem) => {
        setItens((prev) => {
            if (itemEmEdicaoIndex === null) return [...prev, item];
            return prev.map((it, idx) => (idx === itemEmEdicaoIndex ? item : it));
        });
        setItemEmEdicaoIndex(null);
        setNotaFiscalAvulsaId(null);
    };

    const montarPayloadCriar = () => {
        const tipo_avulsa = tipo === 'CIAP' ? 'ciap' : 'entrada';
        const fornecedorId = fornecedor?.value ? Number.parseInt(String(fornecedor.value), 10) : NaN;
        const fornecedorRow =
            Number.isFinite(fornecedorId) && store.fornecedores.arr
                ? store.fornecedores.get(fornecedorId)
                : undefined;
        const fornecedorCnpj = apenasDigitos(fornecedorRow?.cpf_cnpj);
        const numeroParsed = Number.parseInt(String(numeroNota).replace(/\D/g, ''), 10);
        const serieNfeStr = String(serie ?? '').trim();
        const serieParsed = Number.parseInt(serieNfeStr.replace(/\D/g, ''), 10);
        return {
            tipo_avulsa,
            configuracao_nfe_id: configuracaoNfeId as number,
            numero_nota: numeroParsed,
            proximo_numero: numeroParsed,
            ...(serieNfeStr ? { serie_nfe: serieNfeStr } : {}),
            ...(Number.isFinite(serieParsed) ? { serie: serieParsed } : {}),
            fornecedor: String(fornecedorRow?.nome ?? '').trim() || String(fornecedor?.value ?? ''),
            ...(Number.isFinite(fornecedorId) ? { fornecedor_id: fornecedorId } : {}),
            fornecedor_cnpj: fornecedorCnpj,
            data_emissao: dataBrParaIso8601(dataEmissao),
            data_saida_ent: dataBrParaIso8601(dataSaidaEntrada),
            chave_referenciada: chaveReferenciada,
            informacoes_complementares: informacoesComplementares,
            valor_total: valorTotal,
            valor_icms: valorIcmsTotalCalculado,
            forma_pagamento: '01',
            indicador_pagamento: '0',
            itens: itens.map((it) => ({
                codigo_produto: it.codigoProduto,
                descricao: it.descricaoProduto,
                unidade: it.unidade || 'UN',
                quantidade: it.quantidade,
                valor_unitario: it.valorUnitario,
                valor_total: it.valorTotal,
                ncm: it.codigoNcm,
                cfop: it.cfop,
                ibscbs_cst: it.ibscbsCst,
                ibscbs_classe: it.ibscbsClasse,
                icms_cst: it.icmsCst || '00',
                icms_aliquota: it.icmsPercentual,
                ipi_cst: it.ipiCst,
                ipi_aliquota: it.ipiPercentual,
                cofins_cst: it.cofinsCst || '07',
                cofins_aliquota: it.cofinsPercentual,
                pis_cst: it.pisCst || '07',
                pis_aliquota: it.pisPercentual,
            })),
        };
    };

    const validarAntesApi = (): boolean => {
        if (configuracaoNfeId == null) {
            store.notificar(
                'Configuração NFe não encontrada para esta propriedade. Cadastre em Configuração NFe antes de continuar.',
            );
            return false;
        }
        const numeroNotaCheck = Number.parseInt(String(numeroNota).replace(/\D/g, ''), 10);
        if (!Number.isFinite(numeroNotaCheck) || numeroNotaCheck <= 0) {
            store.notificar('Aguarde o carregamento do número da NFe ou verifique a Configuração NFe.');
            return false;
        }
        if (!String(serie ?? '').trim()) {
            store.notificar('Aguarde o carregamento da série da NFe ou verifique a Configuração NFe.');
            return false;
        }
        if (!fornecedor?.value) {
            store.notificar('Selecione o fornecedor.');
            return false;
        }
        const fid = Number.parseInt(String(fornecedor.value), 10);
        const fRow = store.fornecedores.arr ? store.fornecedores.get(fid) : undefined;
        const cnpjOk = apenasDigitos(fRow?.cpf_cnpj);
        if (!cnpjOk || (cnpjOk.length !== 11 && cnpjOk.length !== 14)) {
            store.notificar('O fornecedor selecionado precisa ter CPF/CNPJ válido (11 ou 14 dígitos) cadastrado.');
            return false;
        }
        if (!itens.length) {
            store.notificar('Adicione ao menos um item.');
            return false;
        }
        if (!dataBrValida(dataSaidaEntrada)) {
            store.notificar('Informe a data saída/entrada no formato dd/MM/aaaa (data válida).');
            return false;
        }
        const chaveDigits = (chaveReferenciada || '').replace(/\D/g, '');
        if (chaveDigits.length > 0 && chaveDigits.length !== 44) {
            store.notificar('A chave de acesso referenciada deve conter exatamente 44 dígitos.');
            return false;
        }
        return true;
    };

    const visualizarNfeAvulsa = async () => {
        if (!validarAntesApi()) return;
        setAcaoAvulsaLoading('visualizar');
        try {
            store.toggleLoader();
            const numeroEnviado = Number.parseInt(String(numeroNota).replace(/\D/g, ''), 10);
            const payload = montarPayloadCriar();
            const res = await apiV2.post(`/nfe/avulsa/criar/?prop=${store.propriedade?.id}`, payload);
            const dataRes = res.data || {};
            const id =
                dataRes.nota_fiscal_id ??
                dataRes.notaFiscalId ??
                dataRes.id ??
                dataRes.nota_fiscal?.id ??
                null;
            if (!id) {
                store.notificar('Resposta da API sem identificador da nota. Verifique o contrato do endpoint criar.');
                return;
            }

            let jsonContent = dataRes.json_content ?? dataRes.jsonContent;

            if (!jsonContent?.infNFe) {
                const det = await apiV2.get(`/nfe/${id}/?prop=${store.propriedade?.id}`);
                jsonContent = det.data?.json_content ?? jsonContent;
            }

            if (
                jsonContent?.infNFe?.ide &&
                String(jsonContent.infNFe.ide.nNF) === '0' &&
                Number.isFinite(numeroEnviado) &&
                numeroEnviado > 0
            ) {
                const jc = JSON.parse(JSON.stringify(jsonContent)) as typeof jsonContent;
                jc.infNFe.ide.nNF = String(numeroEnviado);
                jsonContent = jc;
            }

            setNotaFiscalAvulsaId(id);

            if (jsonContent?.infNFe) {
                abrirDanfePreview(jsonContent.infNFe, id);
            } else {
                store.notificar('NFe criada, mas o preview não está disponível (json_content ausente).');
            }
        } catch (error) {
            X9Logger.report(error as Error);
            store.notificar(mensagemErroApi(error, 'Erro ao criar/visualizar NFe avulsa.'));
        } finally {
            store.toggleLoader();
            setAcaoAvulsaLoading(null);
        }
    };

    const emitirNfeAvulsa = async () => {
        if (!notaFiscalAvulsaId) {
            store.notificar('Use primeiro "Visualizar NFE" para gerar a nota, depois envie para a SEFAZ.');
            return;
        }
        setAcaoAvulsaLoading('enviar');
        try {
            store.toggleLoader();
            await apiV2.post(`/nfe/avulsa/emitir/?prop=${store.propriedade?.id}`, {
                nota_fiscal_id: notaFiscalAvulsaId,
            });
            store.notificar('NFe avulsa enviada para emissão com sucesso!');
            onEnvioEmissaoSuccess?.(notaFiscalAvulsaId);
            handleClose();
        } catch (error) {
            X9Logger.report(error as Error);
            store.notificar(mensagemErroApi(error, 'Erro ao emitir NFe avulsa.'));
        } finally {
            store.toggleLoader();
            setAcaoAvulsaLoading(null);
        }
    };

    return (
        <Modal onClose={handleClose} open={open}>
            <ModalHeader>{titulo}</ModalHeader>
            <ModalBody style={{ width: '1000px', maxHeight: '85vh', overflowY: 'auto' }}>
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>N. NFe / Série</div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#0288d1' }}>
                        {numeroNota || '-'} / {serie || '-'}
                    </div>
                </div>

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Select
                            placeholder="Selecione o Fornecedor"
                            value={fornecedor}
                            options={fornecedorOptions}
                            onChange={(e) => {
                                setFornecedor((e as SelectRow) || null);
                                setNotaFiscalAvulsaId(null);
                            }}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <TextField
                            label="Data da Emissão"
                            value={dataEmissao}
                            fullWidth
                            disabled
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="Data Saída/Entrada"
                            value={dataSaidaEntrada}
                            onChange={(e) => {
                                setDataSaidaEntrada(e.target.value);
                                setNotaFiscalAvulsaId(null);
                            }}
                            fullWidth
                            placeholder="dd/MM/aaaa"
                            helperText="Formato: dd/MM/aaaa"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Chave de Acesso Referenciada"
                            value={chaveReferenciada}
                            onChange={(e) => setChaveReferenciada(e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Informações Complementares"
                            value={informacoesComplementares}
                            onChange={(e) => setInformacoesComplementares(e.target.value)}
                            multiline
                            rows={2}
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Box display="flex" flexDirection="column" alignItems="center" mt={2} mb={2}>
                            <Box width="50%">
                                <Botao onClick={abrirNovoItem} disabled={Boolean(acaoAvulsaLoading)}>
                                    Adicionar Item
                                </Botao>
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <div style={{ overflowX: 'hidden', borderRadius: '10px' }}>
                            <MaterialTable<LinhaItemNfeAvulsa>
                                title=""
                                columns={colunasItensTabela}
                                data={dadosItensTabela}
                                options={{
                                    ...config.options,
                                    paging: false,
                                    search: false,
                                    sorting: false,
                                    minBodyHeight: 0,
                                    maxBodyHeight: 440,
                                    pageSize: Math.max(dadosItensTabela.length, 1),
                                    headerStyle: {
                                        backgroundColor: '#f5f5f5',
                                        background: '#f5f5f5',
                                        color: '#000000',
                                        fontWeight: 600,
                                        height: '40px',
                                        paddingTop: 10,
                                        paddingBottom: 10,
                                        fontSize: 14,
                                        borderBottom: '1px solid #e0e0e0',
                                        zIndex: 0,
                                    },
                                }}
                                localization={config.localization}
                                style={config.style}
                            />
                        </div>
                    </Grid>

                    <Grid item xs={6}>
                        <TextField
                            label="Valor Total (R$)"
                            value={Formatter.formatNumber(2, valorTotal)}
                            fullWidth
                            disabled
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="Valor ICMS total (R$)"
                            value={Formatter.formatNumber(2, valorIcmsTotalCalculado)}
                            fullWidth
                            disabled
                        />
                    </Grid>
                </Grid>
            </ModalBody>

            <Footer>
                <Botao onClick={handleClose} variant="contained" cor="#FC7467" disabled={Boolean(acaoAvulsaLoading)}>
                    Cancelar
                </Botao>
                <Botao
                    onClick={visualizarNfeAvulsa}
                    variant="contained"
                    cor="#ff9800"
                    disabled={Boolean(acaoAvulsaLoading)}
                >
                    {acaoAvulsaLoading === 'visualizar' ? 'Aguarde…' : 'Visualizar NFE'}
                </Botao>
                <Botao
                    onClick={emitirNfeAvulsa}
                    variant="contained"
                    cor="#00C853"
                    disabled={Boolean(acaoAvulsaLoading) || !notaFiscalAvulsaId}
                >
                    {acaoAvulsaLoading === 'enviar' ? 'Aguarde…' : 'Enviar'}
                </Botao>
            </Footer>

            <ModalNFEAvulsaItem
                open={modalItemOpen}
                handleClose={() => {
                    setModalItemOpen(false);
                    setItemEmEdicaoIndex(null);
                }}
                item={itemEmEdicaoIndex !== null ? itens[itemEmEdicaoIndex] : null}
                onSave={salvarItem}
            />
        </Modal>
    );
});

export default ModalNFeTeste;